import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Share2, Check, BookOpen, ArrowUp, Minus, Plus, List } from "lucide-react";
import { getBlogPostBySlug, getBlogPosts } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import BlogComments from "../components/site/BlogComments";

const COLORS_TEXT = "#2B241F";
const COLORS_GOLD = "#C8A97E";
const COLORS_MUTED = "#8C8178";
const COLORS_BORDER = "#D8C7B5";

function formatDate(dateStr) {
  if (!dateStr) return "";
  try { return new Date(dateStr).toLocaleDateString("pt-PT", { day: "numeric", month: "long", year: "numeric" }); } catch { return dateStr; }
}

function estReadTime(content) {
  if (!content) return "1 MIN";
  const text = content.replace(/<[^>]*>/g, "");
  const words = text.trim().split(/\s+/).length;
  return `${Math.max(1, Math.ceil(words / 200))} MIN`;
}

const FONT_SIZES = { small: 15, normal: 18, large: 21 };
const FONT_LABELS = { small: "P", normal: "M", large: "G" };

export default function BlogPost() {
  const { slug } = useParams();
  const { lang } = useLang();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const [copied, setCopied] = useState(false);
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [fontSize, setFontSize] = useState(() => localStorage.getItem("blog-font-size") || "normal");
  const [headings, setHeadings] = useState([]);
  const [activeHeading, setActiveHeading] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    setLoading(true); setNotFound(false); setPost(null); setScrollPct(0); setHeadings([]);
    Promise.all([
      getBlogPostBySlug(slug, lang),
      getBlogPosts(lang),
      fetch(`/api/site-data?locale=${lang}`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]).then(([postData, postsData, sd]) => {
      if (!postData) { setNotFound(true); return; }
      setPost(postData);
      setAllPosts(postsData.filter((p) => p.published === 1 || p.published === true));
      if (sd) setSiteData(sd);
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug, lang]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const st = window.scrollY;
      const dh = document.documentElement.scrollHeight - window.innerHeight;
      setScrollPct(dh > 0 ? Math.min(100, (st / dh) * 100) : 0);
      setShowTopBtn(st > 600);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { localStorage.setItem("blog-font-size", fontSize); }, [fontSize]);

  // Parse headings for TOC
  useEffect(() => {
    if (!post?.content) return;
    const timer = setTimeout(() => {
      const el = contentRef.current;
      if (!el) return;
      const hs = el.querySelectorAll("h2, h3");
      const items = [];
      hs.forEach((h, i) => {
        const id = `section-${i}`;
        h.id = id;
        items.push({ id, text: h.textContent, level: h.tagName.toLowerCase(), top: 0 });
      });
      setHeadings(items);
    }, 100);
    return () => clearTimeout(timer);
  }, [post?.content]);

  // IntersectionObserver for TOC
  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { setActiveHeading(e.target.id); break; }
        }
      },
      { rootMargin: "-10% 0px -80% 0px", threshold: 0 }
    );
    headings.forEach((h) => { const el = document.getElementById(h.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [headings]);

  function copyLink() {
    navigator.clipboard.writeText(window.location.href).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  function decreaseFontSize() {
    if (fontSize === "large") setFontSize("normal");
    else if (fontSize === "normal") setFontSize("small");
  }

  function increaseFontSize() {
    if (fontSize === "small") setFontSize("normal");
    else if (fontSize === "normal") setFontSize("large");
  }

  const currentIndex = allPosts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;
  const relatedPosts = allPosts.filter((p) => p.slug !== slug).slice(0, 3);
  const blogPostsFromSite = siteData?.blog_posts || [];
  const currentPostId = blogPostsFromSite.find((p) => p.slug === slug)?.id;

  const globals = siteData?.globals || {};
  const navigation = siteData?.navigation || [];
  const uiStrings = siteData?.ui_strings || {};
  const aboutData = siteData?.about || {};
  const partnerLogos = aboutData?.partner_logos || [];
  const settings = siteData?.settings || {};
  const enabledSections = {
    hero: settings.section_hero_visible !== "false", about: settings.section_about_visible !== "false",
    services: settings.section_services_visible !== "false", portfolio: settings.section_portfolio_visible !== "false",
    testimonials: settings.section_testimonials_visible !== "false", faq: settings.section_faq_visible !== "false",
    blog: settings.section_blog_visible !== "false", contact: settings.section_contact_visible !== "false",
  };
  const elementVisibility = {
    blog_toc: settings.element_blog_toc_visible !== "false",
    blog_comments: settings.element_blog_comments_visible !== "false",
  };
  const hasData = { services: false, portfolio: false, testimonials: false, blog: allPosts.length > 0 };
  const showToc = elementVisibility.blog_toc && headings.length > 3;
  const showComments = elementVisibility.blog_comments;

  if (loading) {
    return (<div data-id="page-blog-post" className="min-h-screen bg-[#F5F1EC] flex items-center justify-center"><div className="font-serif text-2xl animate-pulse" style={{ color: "#2B241F" }}>Hub<span style={{ color: "#C8A97E" }}>.</span></div></div>);
  }
  if (notFound) {
    return (<div data-id="page-blog-post-notfound" className="min-h-screen bg-[#F5F1EC] flex items-center justify-center"><div className="text-center px-6"><p className="font-serif text-5xl" style={{ color: "#C8A97E" }}>404</p><p className="mt-4 font-light" style={{ color: "#8C8178" }}>Artigo não encontrado.</p><Link to="/#blog" className="mt-8 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase hover:opacity-70 transition-opacity" style={{ color: "#2B241F" }}><ArrowLeft size={12} /> Voltar ao início</Link></div></div>);
  }

  return (
    <div data-id="page-blog-post" className="min-h-screen" style={{ backgroundColor: "#F5F1EC" }}>
      <div className="fixed top-0 left-0 right-0 z-[60] h-[2px]" style={{ backgroundColor: "rgba(200,169,126,0.15)" }}><div className="h-full transition-all duration-150 ease-out" style={{ width: `${scrollPct}%`, backgroundColor: "#C8A97E" }} /></div>

      <Header navigation={navigation} globals={globals} hasServices={false} hasPortfolio={false} hasTestimonials={false} hasBlog={allPosts.length > 0} enabledSections={enabledSections} uiStrings={uiStrings} contactHref="/#contato" forceDark />

      <main className="pt-24 md:pt-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 flex gap-10 xl:gap-16">
          {/* Desktop TOC — sticky sidebar */}
          {showToc && (
            <nav data-id="blog-toc" className="hidden xl:block w-44 shrink-0">
              <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pb-8">
                <p className="text-[9px] tracking-[0.22em] uppercase font-medium mb-4" style={{ color: COLORS_TEXT }}>ÍNDICE</p>
                <ul className="space-y-1">
                  {headings.map((h) => (
                    <li key={h.id}>
                      <button onClick={() => document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth" })}
                        className={`block w-full text-left text-xs leading-snug py-1 transition-all duration-300 ${h.level === "h3" ? "pl-3" : ""} ${activeHeading === h.id ? "font-medium" : "font-light"}`}
                        style={{ color: activeHeading === h.id ? COLORS_TEXT : COLORS_MUTED, borderLeft: activeHeading === h.id ? `2px solid ${COLORS_GOLD}` : "2px solid transparent", paddingLeft: h.level === "h3" ? "14px" : "6px" }}>{h.text}</button>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>
          )}

          {/* Main content column */}
          <div className="flex-1 min-w-0">
            <article data-id="blog-post-article" className="max-w-[720px] mx-auto pb-16">
              <Link to="/#blog" className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase hover:opacity-70 transition-opacity mb-4" style={{ color: "#8C8178" }} data-id="blog-post-back"><ArrowLeft size={12} /> Voltar</Link>
              <div className="w-full h-px mb-8 md:mb-10" style={{ backgroundColor: "#D8C7B5" }} />

              {post.category && (
                <span data-id="blog-post-category" className="inline-block px-3 py-1.5 text-[9px] tracking-[0.22em] uppercase mb-5" style={{ color: "#C8A97E", border: "1px solid #D8C7B5" }}>{post.category}</span>
              )}

              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.08] font-light tracking-[-0.01em]" style={{ color: "#2B241F" }} data-id="blog-post-title">{post.title}</h1>

              <div className="flex flex-wrap items-center gap-3 md:gap-5 mt-6 md:mt-10">
                <div className="flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase" style={{ color: "#8C8178" }}><Clock size={13} /><time data-id="blog-post-date">{formatDate(post.created_at)}</time></div>
                <span className="text-[11px] tracking-[0.15em] uppercase" style={{ color: "#C8A97E" }}>{estReadTime(post.content)} DE LEITURA</span>
                {post.author_name && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em]" style={{ color: "#8C8178" }}>
                    {post.author_avatar ? <img src={post.author_avatar} alt={post.author_name} className="w-5 h-5 rounded-full object-cover" /> : <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-medium uppercase" style={{ backgroundColor: "rgba(200,169,126,0.15)", color: "#C8A97E" }}>{post.author_name[0]}</span>}
                    {post.author_name}
                  </span>
                )}
                <div className="flex items-center gap-0.5 ml-auto">
                  {showToc && (
                    <button onClick={() => setTocOpen(!tocOpen)} className={`xl:hidden p-1.5 rounded hover:bg-black/[0.04] transition-colors ${tocOpen ? "bg-black/[0.04]" : ""}`} title="Índice" style={{ color: "#8C8178" }}><List size={13} /></button>
                  )}
                  <button onClick={decreaseFontSize} disabled={fontSize === "small"} className="p-1.5 rounded hover:bg-black/[0.04] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" style={{ color: "#8C8178" }} title="Diminuir texto"><Minus size={14} /></button>
                  <button onClick={increaseFontSize} disabled={fontSize === "large"} className="p-1.5 rounded hover:bg-black/[0.04] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" style={{ color: "#8C8178" }} title="Aumentar texto"><Plus size={14} /></button>
                  <button onClick={copyLink} className="p-1.5 rounded hover:bg-black/[0.04] transition-colors" style={{ color: copied ? "#C8A97E" : "#8C8178" }} title="Copiar link">{copied ? <Check size={13} /> : <Share2 size={13} />}</button>
                </div>
              </div>

              {/* Mobile TOC dropdown */}
              {tocOpen && showToc && (
                <nav className="xl:hidden mt-4 p-4 rounded-sm border" style={{ backgroundColor: "#FAF8F5", borderColor: COLORS_BORDER }}>
                  <p className="text-[9px] tracking-[0.22em] uppercase font-medium mb-3" style={{ color: COLORS_TEXT }}>ÍNDICE</p>
                  <ul className="space-y-1">
                    {headings.map((h) => (
                      <li key={h.id}>
                        <button onClick={() => { document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth" }); setTocOpen(false); }}
                          className={`block w-full text-left text-xs py-1 ${h.level === "h3" ? "pl-3" : ""}`}
                          style={{ color: activeHeading === h.id ? COLORS_TEXT : COLORS_MUTED }}>{h.text}</button>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              {post.image_url && (
                <div data-id="blog-post-hero" className="mt-8 md:mt-10 overflow-hidden">
                  <img src={post.image_url} alt={post.title} className="w-full h-auto max-h-[520px] object-cover" loading="lazy" />
                </div>
              )}

              {post.excerpt && (
                <p className="font-serif text-lg sm:text-xl md:text-2xl leading-relaxed mt-8 md:mt-10 italic font-light pl-4 md:pl-6" style={{ color: "#8C8178", borderLeft: "2px solid rgba(200,169,126,0.3)" }} data-id="blog-post-excerpt">{post.excerpt}</p>
              )}

              <div ref={contentRef} data-id="blog-post-content" className="prose-editorial mt-8 md:mt-10 space-y-5" style={{ fontSize: `${FONT_SIZES[fontSize]}px` }} dangerouslySetInnerHTML={{ __html: post.content }} />
            </article>

            {(prevPost || nextPost) && (
              <nav data-id="blog-post-nav" className="max-w-[720px] mx-auto pb-12">
                <div className="border-t" style={{ borderColor: "#D8C7B5" }}>
                  <div className="grid sm:grid-cols-2" style={{ borderColor: "#D8C7B5" }}>
                    {prevPost ? (
                      <Link to={`/blog/${prevPost.slug}`} className="group flex items-center gap-4 py-6 pr-0 sm:pr-8 hover:bg-black/[0.01] transition-colors duration-300">
                        <div className="shrink-0 w-16 h-12 rounded-sm overflow-hidden" style={{ backgroundColor: "#D8C7B5" }}>{prevPost.image_url ? <img src={prevPost.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: "rgba(200,169,126,0.08)" }}><BookOpen size={14} style={{ color: "#C8A97E" }} /></div>}</div>
                        <div className="min-w-0"><span className="text-[9px] tracking-[0.2em] uppercase block mb-1" style={{ color: "#C8A97E" }}><ArrowLeft size={9} className="inline mr-1" /> Anterior</span><span className="font-serif text-sm leading-snug font-light line-clamp-2 group-hover:opacity-70 transition-opacity" style={{ color: "#2B241F" }}>{prevPost.title}</span></div>
                      </Link>
                    ) : <div />}
                    {nextPost ? (
                      <Link to={`/blog/${nextPost.slug}`} className="group flex items-center justify-end gap-4 text-right py-6 pl-0 sm:pl-8 hover:bg-black/[0.01] transition-colors duration-300" style={{ borderLeft: "1px solid #D8C7B5" }}>
                        <div className="min-w-0"><span className="text-[9px] tracking-[0.2em] uppercase block mb-1" style={{ color: "#C8A97E" }}>Seguinte <ArrowRight size={9} className="inline ml-1" /></span><span className="font-serif text-sm leading-snug font-light line-clamp-2 group-hover:opacity-70 transition-opacity" style={{ color: "#2B241F" }}>{nextPost.title}</span></div>
                        <div className="shrink-0 w-16 h-12 rounded-sm overflow-hidden" style={{ backgroundColor: "#D8C7B5" }}>{nextPost.image_url ? <img src={nextPost.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: "rgba(200,169,126,0.08)" }}><BookOpen size={14} style={{ color: "#C8A97E" }} /></div>}</div>
                      </Link>
                    ) : <div />}
                  </div>
                </div>
              </nav>
            )}

            {relatedPosts.length > 0 && (
              <section data-id="blog-post-related" className="max-w-[720px] mx-auto pb-16">
                <div className="border-t pt-12 md:pt-16" style={{ borderColor: "#D8C7B5" }}>
                  <p className="text-[10px] tracking-[0.28em] uppercase font-medium mb-8 md:mb-10" style={{ color: "#C8A97E" }}>ARTIGOS RELACIONADOS</p>
                  <div className="grid sm:grid-cols-3 gap-6 md:gap-8">
                    {relatedPosts.map((rp) => (
                      <Link key={rp.uid} to={`/blog/${rp.slug}`} className="group block">
                        <div className="aspect-[16/10] rounded-sm overflow-hidden mb-3 md:mb-4" style={{ backgroundColor: "#D8C7B5" }}>{rp.image_url ? <img src={rp.image_url} alt={rp.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: "rgba(200,169,126,0.06)" }}><BookOpen size={20} style={{ color: "#C8A97E" }} /></div>}</div>
                        {rp.category && <span className="text-[8px] tracking-[0.18em] uppercase" style={{ color: "#C8A97E" }}>{rp.category}</span>}
                        <h4 className="font-serif text-sm sm:text-base leading-snug font-light mt-1.5 group-hover:opacity-70 transition-opacity line-clamp-2" style={{ color: "#2B241F" }}>{rp.title}</h4>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {showComments && (
            <section data-id="blog-post-comments" className="max-w-[720px] mx-auto pb-20">
              <div className="border-t pt-12 md:pt-16" style={{ borderColor: "#D8C7B5" }}><BlogComments postId={currentPostId} /></div>
            </section>
            )}
          </div>
        </div>
      </main>

      <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className={`fixed bottom-8 right-6 md:right-10 z-50 w-10 h-10 rounded-full flex items-center justify-center shadow-sm transition-all duration-500 ${showTopBtn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`} style={{ backgroundColor: "#2B241F", color: "#C8A97E" }} aria-label="Voltar ao topo"><ArrowUp size={16} /></button>

      <Footer globals={globals} whatsappNumber={globals.whatsapp_number || ""} navigation={navigation} uiStrings={uiStrings} enabledSections={enabledSections} hasServices={hasData.services} hasPortfolio={hasData.portfolio} hasTestimonials={hasData.testimonials} hasBlog={hasData.blog} partnerLogos={partnerLogos} />
    </div>
  );
}
